import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import type { PerfilTributarioResponse, TabelaPrecoResponse, VigenciaResponse } from '@triade/contracts';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { useAtualizarTabelaPreco, useCriarTabelaPreco, useNovaVigencia, useTabelaPreco, useTabelasPreco } from '@/api/tabelas-preco';
import { useDesvincularCondicao, useMarcarPadraoCondicao, useTabelaCondicoes, useVincularCondicao } from '@/api/tabela-condicao';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { OrigemLucro } from '@/components/origem-lucro';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { formatarContagem, formatarData, formatarFracaoPercentual } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ColunasAntigasItens } from './colunas-antigas';
import { NovaVigenciaEstruturada } from './nova-vigencia';
import { PreviaConversao } from './previa-conversao';
import {
  AVISO_TRIBUTO_NAO_MARCADO,
  BASE_DESPESA_ROTULO,
  DESPESA_VAZIA,
  INCIDENCIA_ROTULO,
  REGIME_ROTULO,
  TAXCODE_ROTULO,
  TIPO_DESPESA_ROTULO,
  TRIBUTO_ROTULO,
  ehLegada,
  entradaDeTabela,
  mensagemErroTabela,
  novaVigenciaVazia,
  paraNovaVigenciaLegada,
  tabelaVazia,
  tributoNaoMarcado,
  validarCriacao,
  type BaseDespesa,
  type DespesaEntrada,
  type DespesaTipo,
  type NovaVigenciaEntrada,
  type TabelaEntrada,
  type TaxCodeEntrada,
} from './tabela-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const BOTAO = 'h-[34px] rounded-[10px] border border-input bg-card px-3 text-small font-semibold text-ink-body hover:bg-line-soft disabled:opacity-50';
const BOTAO_PRIMARIO = 'h-[34px] rounded-[10px] bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const CHIP = 'rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body';

export interface BuscaTabelas {
  id?: string;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaTabelas(bruto: Record<string, unknown>): BuscaTabelas {
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return id ? { id } : {};
}

/** Vigencia em aberto: a de `validTo` nulo (a API devolve as vigencias). */
export function vigenciaAberta(vigencias: VigenciaResponse[] | undefined): VigenciaResponse | undefined {
  return vigencias?.find((v) => v.validTo === null);
}

/** Tela "Tabelas de preço" (/cadastros/tabelas): lista; registro abre por `?id=` (`novo` cria). */
export function TabelasPage({ busca }: { busca: BuscaTabelas }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('tabela.editar');
  const [aviso, setAviso] = useState<string | null>(null);
  const lista = useTabelasPreco({ limit: 200 });
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver as tabelas.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as tabelas" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando tabelas…</Mensagem>;
  else if (itens.length === 0) {
    corpo = (
      <Mensagem papel="status">
        Nenhuma tabela de preço cadastrada.
        {podeEditar && (
          <>
            {' '}
            <Link to="/cadastros/tabelas" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre a primeira
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-small" aria-label="Lista de tabelas de preço">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Identificador</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Padrão</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Situação</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Vigência atual (meta · mínimo)</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Despesas</th>
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((t) => (
              <LinhaTabela key={t.id} tabela={t} selecionada={busca.id === t.id} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Tabelas de preço
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Tabelas de preço</h1>
        </div>
      </div>

      <section aria-label="Lista de tabelas" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {corpo}
      </section>

      {busca.id === 'novo' && !podeEditar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar tabelas de preço.
        </section>
      ) : (
        busca.id && (
          <>
            {aviso && <p role="status" className="rounded-xl border border-border bg-card px-4 py-3 text-small text-ink-body">{aviso}</p>}
            <TabelaDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} podeEditar={podeEditar} onAviso={setAviso} />
          </>
        )
      )}
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Nova tabela', onClick: () => void navigate({ to: '/cadastros/tabelas', search: { id: 'novo' } }) } : undefined}
        paginacao={lista.data ? pagItens.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="tabelas" />}
      />
    </div>
  );
}

function LinhaTabela({ tabela, selecionada }: { tabela: TabelaPrecoResponse; selecionada: boolean }) {
  const navigate = useNavigate();
  /** A lista nao traz vigencias: o detalhe de cada linha traz a vigencia em aberto (lucro, minimo, origem, despesas). */
  const doc = useTabelaPreco(tabela.id);
  const atual = vigenciaAberta(doc.data?.vigencias);
  const abrir = () => void navigate({ to: '/cadastros/tabelas', search: { id: tabela.id } });

  let celulaVigencia;
  if (!doc.data) celulaVigencia = <span className="text-ink-muted">{doc.isError ? '—' : '…'}</span>;
  else if (!atual) celulaVigencia = <span className="text-ink-muted">sem vigência em aberto</span>;
  else
    celulaVigencia = (
      <span className="flex flex-wrap items-center gap-2">
        <span className="tabular">{`${formatarFracaoPercentual(atual.lucroDesejado)} · mín. ${formatarFracaoPercentual(atual.lucroMinimo)}`}</span>
        <OrigemLucro desejado={atual.lucroDesejadoOrigem} minimo={atual.lucroMinimoOrigem} compacto />
      </span>
    );

  return (
    <tr
      data-testid="linha-tabela"
      onClick={abrir}
      className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', selecionada && 'bg-line-soft')}
    >
      <td className="px-4 py-3 font-semibold">{tabela.codigo}</td>
      <td className="px-3 py-3">{tabela.nome}</td>
      <td className="px-3 py-3">{tabela.isDefault ? <span className={CHIP}>padrão</span> : '—'}</td>
      <td className="px-3 py-3">
        <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', tabela.ativa ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
          {tabela.ativa ? 'Ativa' : 'Inativa'}
        </span>
      </td>
      <td className="px-3 py-3">{celulaVigencia}</td>
      <td className="tabular px-4 py-3 text-ink-secondary">{atual?.despesas ? formatarContagem(atual.despesas.length) : '—'}</td>
    </tr>
  );
}

/** Registro de uma tabela (ou nova). Abas: Dados, Vigência atual e histórico, Nova vigência, Condições disponíveis. */
function TabelaDetalhe({ id, podeEditar, onAviso }: { id: string | undefined; podeEditar: boolean; onAviso: (texto: string) => void }) {
  const doc = useTabelaPreco(id);
  const t = doc.data;

  if (id && !t) {
    return (
      <section className="rounded-2xl border border-border bg-card p-5">
        {doc.isError ? <ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível carregar a tabela" /> : <p role="status" className="text-small text-ink-muted">Carregando tabela…</p>}
      </section>
    );
  }

  return (
    <section aria-label="Registro da tabela" className="rounded-2xl border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-title font-semibold">{t ? `${t.codigo} · ${t.nome}` : 'Nova tabela de preço'}</h2>
      </div>
      <Tabs defaultValue="dados" className="p-5">
        <TabsList className="mb-4 flex flex-wrap gap-1">
          <TabsTrigger value="dados">Dados</TabsTrigger>
          {t && <TabsTrigger value="historico">Vigência atual e histórico</TabsTrigger>}
          {t && <TabsTrigger value="nova">Nova vigência</TabsTrigger>}
          {t && <TabsTrigger value="condicoes">Condições disponíveis</TabsTrigger>}
        </TabsList>
        {/* forceMount + hidden: o que foi digitado numa aba sobrevive a troca de aba. */}
        <TabsContent value="dados" forceMount className="data-[state=inactive]:hidden">
          <DadosTab tabela={t} podeEditar={podeEditar} onAviso={onAviso} />
        </TabsContent>
        {t && (
          <>
            <TabsContent value="historico" forceMount className="data-[state=inactive]:hidden">
              <HistoricoTab tabela={t} podeEditar={podeEditar} onAviso={onAviso} />
            </TabsContent>
            <TabsContent value="nova" forceMount className="data-[state=inactive]:hidden">
              <NovaVigenciaTab tabelaId={t.id} podeEditar={podeEditar} vigenciaAberta={vigenciaAberta(t.vigencias)} onAviso={onAviso} />
            </TabsContent>
            <TabsContent value="condicoes" forceMount className="data-[state=inactive]:hidden">
              <CondicoesTab tabelaId={t.id} podeEditar={podeEditar} onAviso={onAviso} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </section>
  );
}

function DadosTab({ tabela, podeEditar, onAviso }: { tabela: TabelaPrecoResponse | undefined; podeEditar: boolean; onAviso: (texto: string) => void }) {
  const navigate = useNavigate();
  const criar = useCriarTabelaPreco();
  const atualizar = useAtualizarTabelaPreco();
  const [valores, setValores] = useState<TabelaEntrada>(tabela ? entradaDeTabela(tabela) : tabelaVazia());
  const [erro, setErro] = useState<string | null>(null);
  const salvando = criar.isPending || atualizar.isPending;

  useEffect(() => {
    setValores(tabela ? entradaDeTabela(tabela) : tabelaVazia());
  }, [tabela?.id, tabela?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  async function gravar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!tabela) {
      const r = validarCriacao(valores);
      if (!r.ok) return setErro(r.mensagem);
      try {
        const criada = await criar.mutateAsync(r.dados);
        onAviso('Tabela criada. Registre a primeira vigência na aba Nova vigência.');
        void navigate({ to: '/cadastros/tabelas', search: { id: criada.id }, replace: true });
      } catch (err) {
        setErro(mensagemErroTabela(err));
      }
      return;
    }
    const nome = valores.nome.trim();
    if (nome === '') return setErro('Informe o nome da tabela');
    try {
      await atualizar.mutateAsync({
        id: tabela.id,
        nome,
        isDefault: valores.isDefault,
        ativa: valores.ativa,
        precoIncluiCbsIbs: valores.precoIncluiCbsIbs,
      });
      onAviso('Dados da tabela salvos.');
    } catch (err) {
      setErro(mensagemErroTabela(err));
    }
  }

  return (
    <form onSubmit={gravar} aria-label="Dados da tabela" className="flex max-w-[640px] flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="tabela-codigo" className={ROTULO}>Identificador</label>
          <input
            id="tabela-codigo"
            className={CAMPO}
            value={valores.codigo}
            disabled={!!tabela || !podeEditar}
            maxLength={40}
            onChange={(e) => setValores({ ...valores, codigo: e.target.value })}
          />
          {tabela && <p className="mt-1 text-caption text-ink-muted">O identificador não muda depois de salvo.</p>}
        </div>
        <div>
          <label htmlFor="tabela-nome" className={ROTULO}>Nome</label>
          <input id="tabela-nome" className={CAMPO} value={valores.nome} disabled={!podeEditar} maxLength={200} onChange={(e) => setValores({ ...valores, nome: e.target.value })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-6 text-small text-ink-body">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={valores.isDefault} disabled={!podeEditar} onChange={(e) => setValores({ ...valores, isDefault: e.target.checked })} />
          Tabela padrão
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={valores.ativa} disabled={!podeEditar} onChange={(e) => setValores({ ...valores, ativa: e.target.checked })} />
          Ativa
        </label>
      </div>
      <div>
        <label className="flex items-center gap-2 text-small text-ink-body">
          <input
            type="checkbox"
            aria-describedby="tabela-cbs-ajuda"
            checked={valores.precoIncluiCbsIbs}
            disabled={!podeEditar}
            onChange={(e) => setValores({ ...valores, precoIncluiCbsIbs: e.target.checked })}
          />
          Preço inclui CBS/IBS
        </label>
        <p id="tabela-cbs-ajuda" className="mt-1 text-caption text-ink-muted">
          Marque para tabelas de venda a não contribuintes: o preço praticado digitado já é o total da NF. Padrão: desmarcado.
        </p>
      </div>
      {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
      {podeEditar ? (
        <div>
          <button type="submit" className={BOTAO_PRIMARIO} disabled={salvando}>
            {tabela ? 'Salvar dados' : 'Criar tabela'}
          </button>
        </div>
      ) : (
        <p className="text-small text-ink-muted">Seu acesso permite ver a tabela, não alterá-la.</p>
      )}
    </form>
  );
}

function HistoricoTab({ tabela, podeEditar, onAviso }: { tabela: TabelaPrecoResponse; podeEditar: boolean; onAviso: (texto: string) => void }) {
  const { pode } = usePode();
  // Sessao 51b (U8): bloco de colunas antigas so na vigencia estruturada em aberto da tabela padrao, com tributo.editar e custo.ver.
  const podeColunasAntigas = pode('tributo.editar') && pode('custo.ver');
  const vigencias = [...(tabela.vigencias ?? [])].sort((a, b) => (a.validFrom < b.validFrom ? 1 : -1));
  if (vigencias.length === 0) return <p className="text-small text-ink-muted">Esta tabela ainda não tem vigência.</p>;

  return (
    <ol aria-label="Histórico de vigências" className="flex flex-col gap-3">
      {vigencias.map((v) => {
        const aberta = v.validTo === null;
        return (
          <li key={v.id} data-testid="vigencia" className="rounded-xl border border-border p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-semibold">{`${formatarData(v.validFrom)} até ${v.validTo ? formatarData(v.validTo) : 'em aberto'}`}</span>
              {aberta && <span className={CHIP}>vigente</span>}
              {ehLegada(v) && <span className={CHIP} data-testid="selo-legado">legado</span>}
            </div>
            {ehLegada(v) && aberta && (
              <div className="mt-3">
                <PreviaConversao tabelaId={tabela.id} podeEditar={podeEditar} onConcluida={onAviso} />
              </div>
            )}
            {v.perfilTributario && <ResumoPerfil perfil={v.perfilTributario} />}
            {aberta && !ehLegada(v) && tabela.isDefault && podeColunasAntigas && <ColunasAntigasItens />}
            <dl className="mt-3 grid gap-x-6 gap-y-2 text-small sm:grid-cols-3">
              <div>
                <dt className="text-caption text-ink-muted">Lucro desejado</dt>
                <dd className="tabular" data-testid="lucro-desejado">{formatarFracaoPercentual(v.lucroDesejado)}</dd>
              </div>
              <div>
                <dt className="text-caption text-ink-muted">Lucro mínimo</dt>
                <dd className="tabular" data-testid="lucro-minimo">{formatarFracaoPercentual(v.lucroMinimo)}</dd>
              </div>
              <div>
                <dt className="text-caption text-ink-muted">Origem</dt>
                <dd>
                  <OrigemLucro desejado={v.lucroDesejadoOrigem} minimo={v.lucroMinimoOrigem} />
                </dd>
              </div>
            </dl>
            <DespesasDaVigencia vigencia={v} />
          </li>
        );
      })}
    </ol>
  );
}

/** Despesas da vigencia e o total que a API calculou. So aparecem com custo.ver (VENDEDOR/LEITURA: sem o bloco). */
function DespesasDaVigencia({ vigencia }: { vigencia: VigenciaResponse }) {
  if (!vigencia.despesas) return null;
  return (
    <div className="mt-3 border-t border-line-soft pt-3">
      {vigencia.despesas.length === 0 ? (
        <p className="text-small text-ink-muted">Sem despesas variáveis.</p>
      ) : (
        <table className="w-full text-small" aria-label={`Despesas de ${formatarData(vigencia.validFrom)}`}>
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-1 font-medium">Tipo</th>
              <th scope="col" className="py-1 font-medium">Descrição</th>
              <th scope="col" className="py-1 font-medium">É tributo?</th>
              <th scope="col" className="py-1 font-medium">Base</th>
              <th scope="col" className="py-1 text-right font-medium">%</th>
            </tr>
          </thead>
          <tbody>
            {vigencia.despesas.map((d) => (
              <tr key={d.ordem} className="border-t border-line-soft">
                <td className="py-1.5 text-ink-secondary">{TIPO_DESPESA_ROTULO[d.kind]}</td>
                <td className="py-1.5">{d.description}</td>
                <td className="py-1.5">{d.taxCode ? TAXCODE_ROTULO[d.taxCode] : '—'}</td>
                <td className="py-1.5">{BASE_DESPESA_ROTULO[d.base]}</td>
                <td className="tabular py-1.5 text-right">{formatarFracaoPercentual(d.rate)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border font-semibold">
              <td colSpan={4} className="py-1.5">Total das despesas</td>
              <td className="tabular py-1.5 text-right" data-testid="total-despesas">{formatarFracaoPercentual(vigencia.totalDespesas)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

/** Aba "Nova vigência": estruturada por padrao. So a vigência em aberto LEGADA abre o formulario antigo (tabela nova nunca recebe LEGADO). */
function NovaVigenciaTab({ tabelaId, podeEditar, vigenciaAberta: aberta, onAviso }: { tabelaId: string; podeEditar: boolean; vigenciaAberta: VigenciaResponse | undefined; onAviso: (texto: string) => void }) {
  if (aberta && ehLegada(aberta)) {
    return (
      <div className="flex flex-col gap-4">
        <p role="note" className="rounded-xl border border-border px-4 py-3 text-small text-ink-body">
          A vigência em aberto é legada (tributo como despesa variável). Para o modo estruturado, use "Nova vigência estruturada a partir desta" na aba Vigência atual e histórico.
        </p>
        <NovaVigenciaLegadaTab tabelaId={tabelaId} podeEditar={podeEditar} onAviso={onAviso} />
      </div>
    );
  }
  return <NovaVigenciaEstruturada tabelaId={tabelaId} podeEditar={podeEditar} vigenciaAberta={aberta} onAviso={onAviso} />;
}

/** Resumo somente leitura do perfil tributario de uma vigencia estruturada (valores da API, sem conta). */
function ResumoPerfil({ perfil }: { perfil: PerfilTributarioResponse }) {
  return (
    <section className="mt-3 border-t border-line-soft pt-3" aria-label="Perfil tributário da vigência">
      <dl className="grid gap-x-6 gap-y-2 text-small sm:grid-cols-3">
        <div>
          <dt className="text-caption text-ink-muted">Regime</dt>
          <dd>{REGIME_ROTULO[perfil.regimeApuracao]}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">Meta de lucro</dt>
          <dd>{perfil.metaLucro === 'ANTES_IR' ? 'antes do IR' : 'líquida de IR e CSLL'}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">UF de origem</dt>
          <dd>{perfil.ufOrigem ?? 'da empresa'}</dd>
        </div>
      </dl>
      {perfil.tributos.length > 0 && (
        <ul aria-label="Linhas de tributo da vigência" className="mt-2 flex flex-col gap-1 text-small">
          {perfil.tributos.map((t) => (
            <li key={t.tributo}>
              {`${TRIBUTO_ROTULO[t.tributo] ?? t.tributo}: ${formatarFracaoPercentual(t.aliquota)} · ${INCIDENCIA_ROTULO[t.incidencia]}${t.ativo ? '' : ' · desligado'}`}
            </li>
          ))}
        </ul>
      )}
      {perfil.icmsFixo && <p className="mt-2 text-caption text-ink-secondary">ICMS fixo em todas as UFs (DIFAL e interestadual não são calculados).</p>}
      {perfil.icmsUf.length > 0 && <p className="mt-1 text-caption text-ink-secondary">{`Exceções de ICMS por UF: ${perfil.icmsUf.map((x) => x.uf).join(', ')}`}</p>}
    </section>
  );
}

function NovaVigenciaLegadaTab({ tabelaId, podeEditar, onAviso }: { tabelaId: string; podeEditar: boolean; onAviso: (texto: string) => void }) {
  const nova = useNovaVigencia();
  const [v, setV] = useState<NovaVigenciaEntrada>(novaVigenciaVazia);
  const [erro, setErro] = useState<string | null>(null);

  if (!podeEditar) return <p className="text-small text-ink-body" role="status">Seu acesso não permite registrar vigências.</p>;

  const atualizarDespesa = (i: number, parcial: Partial<DespesaEntrada>) =>
    setV({ ...v, despesas: v.despesas.map((d, k) => (k === i ? { ...d, ...parcial } : d)) });

  async function gravar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const r = paraNovaVigenciaLegada(v);
    if (!r.ok) return setErro(r.mensagem);
    try {
      await nova.mutateAsync({ tabelaId, ...r.dados });
      onAviso('Nova vigência registrada. A anterior foi encerrada na data de início.');
      setV(novaVigenciaVazia());
    } catch (err) {
      setErro(mensagemErroTabela(err));
    }
  }

  return (
    <form onSubmit={gravar} aria-label="Nova vigência" className="flex flex-col gap-5">
      <p className="text-small text-ink-secondary">Valores nunca são editados: mudar gera uma nova vigência, a partir da data de início (nada retroativo).</p>
      <div className="grid gap-4 sm:grid-cols-4">
        <div>
          <label htmlFor="vig-inicio" className={ROTULO}>Início</label>
          <input id="vig-inicio" type="date" className={CAMPO} min={novaVigenciaVazia().validFrom} value={v.validFrom} onChange={(e) => setV({ ...v, validFrom: e.target.value })} />
        </div>
        <div>
          <label htmlFor="vig-desejado" className={ROTULO}>Lucro desejado (%)</label>
          <input id="vig-desejado" inputMode="decimal" className={CAMPO} placeholder="ex.: 20" value={v.lucroDesejado} onChange={(e) => setV({ ...v, lucroDesejado: e.target.value })} />
          <p className="mt-1 text-caption text-ink-muted">Vazio = usa o padrão da empresa</p>
        </div>
        <div>
          <label htmlFor="vig-minimo" className={ROTULO}>Lucro mínimo (%)</label>
          <input id="vig-minimo" inputMode="decimal" className={CAMPO} placeholder="ex.: 10" value={v.lucroMinimo} onChange={(e) => setV({ ...v, lucroMinimo: e.target.value })} />
          <p className="mt-1 text-caption text-ink-muted">Vazio = usa o padrão da empresa</p>
        </div>
        <div>
          <label htmlFor="vig-motivo" className={ROTULO}>Motivo</label>
          <input id="vig-motivo" className={CAMPO} maxLength={300} value={v.motivo} onChange={(e) => setV({ ...v, motivo: e.target.value })} />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-caption font-medium text-ink-secondary">Despesas variáveis</span>
          <button type="button" className={BOTAO} onClick={() => setV({ ...v, despesas: [...v.despesas, { ...DESPESA_VAZIA }] })}>
            Adicionar despesa
          </button>
        </div>
        {v.despesas.length === 0 ? (
          <p className="text-small text-ink-muted">Sem despesas nesta vigência.</p>
        ) : (
          <table className="w-full text-small" aria-label="Grade de despesas variáveis">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="py-1 font-medium">Tipo</th>
                <th scope="col" className="py-1 font-medium">Descrição</th>
                <th scope="col" className="py-1 font-medium">É tributo?</th>
                <th scope="col" className="py-1 font-medium">Base</th>
                <th scope="col" className="py-1 font-medium">%</th>
                <th scope="col" className="py-1"><span className="sr-only">Ações</span></th>
              </tr>
            </thead>
            <tbody>
              {v.despesas.map((d, i) => (
                <tr key={i} className="border-t border-line-soft">
                  <td className="py-1.5 pr-2">
                    <select aria-label={`Tipo da despesa ${i + 1}`} className={CAMPO} value={d.tipo} onChange={(e) => atualizarDespesa(i, { tipo: e.target.value as DespesaTipo })}>
                      {(Object.keys(TIPO_DESPESA_ROTULO) as DespesaTipo[]).map((k) => (
                        <option key={k} value={k}>{TIPO_DESPESA_ROTULO[k]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input aria-label={`Descrição da despesa ${i + 1}`} className={CAMPO} maxLength={200} value={d.descricao} onChange={(e) => atualizarDespesa(i, { descricao: e.target.value })} />
                  </td>
                  <td className="w-32 py-1.5 pr-2">
                    <select aria-label={`É tributo? despesa ${i + 1}`} className={CAMPO} value={d.taxCode} onChange={(e) => atualizarDespesa(i, { taxCode: e.target.value as TaxCodeEntrada })}>
                      {(Object.keys(TAXCODE_ROTULO) as TaxCodeEntrada[]).map((k) => (
                        <option key={k || 'nenhum'} value={k}>{TAXCODE_ROTULO[k]}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-caption text-ink-muted">Só para tributo sobre a venda (ICMS, ISS, PIS, COFINS).</p>
                  </td>
                  <td className="w-44 py-1.5 pr-2">
                    <select aria-label={`Base da despesa ${i + 1}`} className={CAMPO} value={d.base} onChange={(e) => atualizarDespesa(i, { base: e.target.value as BaseDespesa })}>
                      {(Object.keys(BASE_DESPESA_ROTULO) as BaseDespesa[]).map((k) => (
                        <option key={k} value={k}>{BASE_DESPESA_ROTULO[k]}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-caption text-ink-muted">Total da NF: o % incide sobre o total com tributos.</p>
                  </td>
                  <td className="w-32 py-1.5 pr-2">
                    <input aria-label={`Percentual da despesa ${i + 1}`} inputMode="decimal" className={CAMPO} placeholder="ex.: 18" value={d.percentual} onChange={(e) => atualizarDespesa(i, { percentual: e.target.value })} />
                  </td>
                  <td className="w-10 py-1.5">
                    <button type="button" aria-label={`Remover despesa ${i + 1}`} className="rounded-md p-1 text-ink-muted hover:bg-line-soft" onClick={() => setV({ ...v, despesas: v.despesas.filter((_, k) => k !== i) })}>
                      <X aria-hidden="true" className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {v.despesas.some(tributoNaoMarcado) && (
          <p role="alert" className="mt-2 text-small text-danger-ink">{AVISO_TRIBUTO_NAO_MARCADO}</p>
        )}
        <p className="mt-2 text-caption text-ink-muted">O total é calculado ao salvar.</p>
      </div>

      {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
      <div>
        <button type="submit" className={BOTAO_PRIMARIO} disabled={nova.isPending}>
          Registrar nova vigência
        </button>
      </div>
    </form>
  );
}

function CondicoesTab({ tabelaId, podeEditar, onAviso }: { tabelaId: string; podeEditar: boolean; onAviso: (texto: string) => void }) {
  const ligadas = useTabelaCondicoes(tabelaId);
  const condicoes = useCondicoesPagamento({ limit: 200 });
  const vincular = useVincularCondicao();
  const marcar = useMarcarPadraoCondicao();
  const desvincular = useDesvincularCondicao();
  const [escolhida, setEscolhida] = useState('');
  const [padrao, setPadrao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const nomes = useMemo(() => new Map((condicoes.data ?? []).map((c) => [c.id, c])), [condicoes.data]);
  const linhas = useMemo(
    () =>
      (ligadas.data ?? []).map((l) => ({ ...l, codigo: nomes.get(l.condicaoId)?.codigo ?? '—', nome: nomes.get(l.condicaoId)?.nome ?? 'condição não encontrada' }))
        .sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR', { numeric: true })),
    [ligadas.data, nomes],
  );
  const disponiveis = (condicoes.data ?? []).filter((c) => c.ativa && !(ligadas.data ?? []).some((l) => l.condicaoId === c.id));

  async function executar(acao: () => Promise<unknown>, sucesso: string) {
    setErro(null);
    try {
      await acao();
      onAviso(sucesso);
    } catch (err) {
      setErro(mensagemErroTabela(err));
    }
  }

  async function vincularAgora(e: React.FormEvent) {
    e.preventDefault();
    if (!escolhida) return setErro('Escolha uma condição');
    await executar(async () => {
      await vincular.mutateAsync({ tabelaId, condicaoId: escolhida, isDefault: padrao });
      setEscolhida('');
      setPadrao(false);
    }, 'Condição vinculada à vigência em aberto.');
  }

  if (ligadas.isError && !ligadas.data) return <ErroLista erro={ligadas.error} onTentar={() => void ligadas.refetch()} titulo="Não foi possível carregar as condições da tabela" />;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-small text-ink-secondary" role="note">Condição ligada só vale na vigência em aberto.</p>

      {!ligadas.data ? (
        <p role="status" className="text-small text-ink-muted">Carregando condições…</p>
      ) : linhas.length === 0 ? (
        <p className="text-small text-ink-muted">Nenhuma condição ligada a esta tabela.</p>
      ) : (
        <ul aria-label="Condições da tabela" className="flex flex-col divide-y divide-line-soft rounded-xl border border-border">
          {linhas.map((l) => (
            <li key={l.condicaoId} data-testid="condicao-tabela" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{l.codigo}</span>
                <span>{l.nome}</span>
                {l.isDefault && <span className={CHIP}>padrão</span>}
              </span>
              {podeEditar && (
                <span className="flex gap-2">
                  {!l.isDefault && (
                    <button
                      type="button"
                      className={BOTAO}
                      disabled={marcar.isPending}
                      onClick={() => void executar(() => marcar.mutateAsync({ tabelaId, condicaoId: l.condicaoId, isDefault: true }), 'Condição padrão da tabela alterada.')}
                    >
                      Tornar padrão
                    </button>
                  )}
                  <button
                    type="button"
                    className={BOTAO}
                    disabled={desvincular.isPending}
                    onClick={() => void executar(() => desvincular.mutateAsync({ tabelaId, condicaoId: l.condicaoId }), 'Condição removida da vigência em aberto.')}
                  >
                    Remover
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {podeEditar && (
        <form onSubmit={vincularAgora} aria-label="Vincular condição" className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px] flex-1">
            <label htmlFor="vincular-condicao" className={ROTULO}>Condição</label>
            <select id="vincular-condicao" className={CAMPO} value={escolhida} onChange={(e) => setEscolhida(e.target.value)}>
              <option value="">Escolha uma condição ativa</option>
              {disponiveis.map((c) => (
                <option key={c.id} value={c.id}>{`${c.codigo} · ${c.nome}`}</option>
              ))}
            </select>
          </div>
          <label className="flex h-[34px] items-center gap-2 text-small text-ink-body">
            <input type="checkbox" checked={padrao} onChange={(e) => setPadrao(e.target.checked)} />
            Tornar padrão
          </label>
          <button type="submit" className={BOTAO_PRIMARIO} disabled={vincular.isPending}>
            Vincular condição
          </button>
        </form>
      )}

      {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
